import React, { useState } from 'react';
import { Table, Spin, Tag, Descriptions, Button, Form, Space, Input, InputNumber, DatePicker, Radio } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs from 'dayjs';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../../Models/Utilisateur';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';
import { Box, Typography } from '@mui/material';
import SearchInput from '../../../SearchInput';
import SearchModal from '../../../SearchModal';
import Params from '../../../../Types/ParamsPicklist';
import formatNumber from '../../../../Functions/Traitement/FormatNumber';

const DemandeReechelonnement = () => {
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<'simu' | 'saisie'>('simu');
    const [pret, setPret] = useState<any>(null);
    const [simus, setSimus] = useState<any[]>([]);
    const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
    const [detail, setDetail] = useState<any>(null);
    const [preview, setPreview] = useState<any[]>([]);

    let searchPret: Params = { CodeAgence: '', Table: "PRETS", Colonne1: "NUMDOS", Colonne2: "NOMCLI", Colagence: '101', Choix: 22 };
    let searchPeriod: Params = { CodeAgence: '', Table: "v_period_lib", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: '101', Choix: 22 };
    let searchMotif: Params = { CodeAgence: '', Table: "v_motif_reech_simu", Colonne1: "CODMOTIF", Colonne2: "LIBMOTIF", Colagence: '101', Choix: 22 };

    function buildPayload(action: string, extra: any = {}) {
        const d = form.getFieldValue('DAT1ECHE_NEW');
        return Crypter.encrypt(JSON.stringify({
            action,
            numDos: form.getFieldValue('NUMDOS') ?? '',
            idReech: mode === 'simu' ? selectedKey : null,
            nbrEche: mode === 'saisie' ? form.getFieldValue('NBRECHE_NEW') : null,
            periodic: mode === 'saisie' ? form.getFieldValue('PERIODIC_NEW') : null,
            dat1Eche: mode === 'saisie' ? (dayjs.isDayjs(d) ? d.format('YYYY-MM-DD') : d) : null,
            motif: mode === 'saisie' ? form.getFieldValue('MOTIF') : null,
            coment: mode === 'saisie' ? form.getFieldValue('COMMENTAIRE') : null,
            utiVal: user.coduti,
            codAge: user.codage,
            ...extra
        }));
    }

    function callApi(action: string, extra: any = {}, onSuccess?: (res: any) => void, silent = false) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.ReschedulePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: buildPayload(action, extra)
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    if (onSuccess) onSuccess(res);
                    else if (!silent) SweetAlert.fire({ title: res.Message, icon: "success" });
                } else if (!silent) {
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    // ---------------- Preview du nouveau capital (non persiste) ----------------
    function previewCapital(thenConfirm: () => void) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.ReschedulePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action: 'CAPITAL', numDos: form.getFieldValue('NUMDOS') ?? '',
                utiVal: user.coduti, codAge: user.codage
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode != 200) { SweetAlert.fire({ title: res.Message, icon: "warning" }); return; }
                const c = res.Result?.Table?.[0];
                SweetAlert.fire({
                    title: "Confirmer la demande de rééchelonnement ?",
                    html: `<table style="margin:auto;text-align:left;font-size:14px">
                        <tr><td>Solde capital restant</td><td style="text-align:right"><b>${formatNumber(c?.soldeCapital ?? 0)}</b></td></tr>
                        <tr><td>+ Intérêts impayés capitalisés</td><td style="text-align:right"><b>${formatNumber(c?.interetsCapitalises ?? 0)}</b></td></tr>
                        <tr><td>+ Pénalités capitalisées</td><td style="text-align:right"><b>${formatNumber(c?.penalitesCapitalisees ?? 0)}</b></td></tr>
                        <tr style="border-top:1px solid #116599"><td><b>= Nouveau capital</b></td><td style="text-align:right;color:#116599"><b>${formatNumber(c?.nouveauCapital ?? 0)}</b></td></tr>
                        <tr><td>Frais de rééchelonnement${c?.codOpe ? ` (${c.codOpe})` : ''}</td><td style="text-align:right"><b>${formatNumber(c?.frais ?? 0)}</b></td></tr>
                    </table>`,
                    showCancelButton: true, confirmButtonText: "Soumettre", cancelButtonText: "Annuler",
                    confirmButtonColor: "#116599", icon: 'question', width: 480
                }).then((r) => { if (r.isConfirmed) thenConfirm(); });
            })
            .catch(() => setLoading(false));
    }

    // ---------------- Consultation du pret ----------------
    function consult() {
        const numdos = form.getFieldValue('NUMDOS');
        if (!numdos) return;
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.GetPretConsult,
            headers: { 'Authorization': authHeader(), 'CipherNumDos': Crypter.encrypt(numdos) }
        })
            .then((response) => {
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) setPret(res.Result?.Table?.[0] ?? null);
            })
            .catch(() => { });
    }

    // ---------------- Simulations enregistrees du dossier ----------------
    function loadSimus() {
        callApi('LISTESIMU', {}, (res) => {
            setSimus(res.Result?.Table ?? []);
            setDetail(null);
            setSelectedKey(null);
            if (!(res.Result?.Table?.length > 0))
                SweetAlert.fire({ title: "Aucune simulation enregistrée pour ce dossier", icon: "info" });
        });
    }

    function loadDetail(id: any) {
        callApi('DETAIL', { idReech: id }, (res) => {
            setDetail({
                demande: res.Result?.Table?.[0] ?? null,
                echeances: res.Result?.Table1 ?? []
            });
        });
    }

    // ---------------- Apercu (saisie directe, sans enregistrement) ----------------
    function doPreview() {
        form.validateFields(['NBRECHE_NEW', 'PERIODIC_NEW', 'DAT1ECHE_NEW']).then(() => {
            callApi('PREVIEW', {}, (res) => {
                // sp_AnySimulerEcheancier : Table=msg, Table1=lignes, Table2=totaux
                const sched = [res.Result?.Table1, res.Result?.Table]
                    .find((t: any[]) => Array.isArray(t) && t.length > 0 && t[0].numSeq !== undefined);
                setPreview(sched ?? []);
            }, false);
        }).catch(() => SweetAlert.fire({ title: "Renseignez échéances, périodicité et 1ère échéance", icon: "warning" }));
    }

    const colsSimu: ColumnsType<any> = [
        { title: 'ID', dataIndex: 'Id' },
        { title: 'Dossier', dataIndex: 'NUMDOS' },
        { title: 'Client', dataIndex: 'NOMCLI' },
        { title: 'Nouv. capital', dataIndex: 'MNTCAPIT', render: (v: number) => formatNumber(v) },
        { title: 'Échéances', dataIndex: 'NBRECHE' },
        { title: 'Périod.', dataIndex: 'PERIODIC' },
        { title: '1ère éch.', dataIndex: 'DAT1ECHE', render: (v: string) => v ? String(v).substring(0, 10) : '' },
        { title: 'Frais', dataIndex: 'MTNFRAIS', render: (v: number) => formatNumber(v) },
        { title: 'Version', dataIndex: 'VERSIONECP' },
        { title: 'Par', dataIndex: 'UTILIS' },
        { title: 'Le', dataIndex: 'DATCRE', render: (v: string) => v ? String(v).substring(0, 16).replace('T', ' ') : '' },
    ];

    const colsEch: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'numSeq' },
        { title: 'Date', dataIndex: 'dateEcheacnce' },
        { title: 'Capital', dataIndex: 'capital', render: (v: number) => formatNumber(v) },
        { title: 'Amort.', dataIndex: 'amortissement', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'interet', render: (v: number) => formatNumber(v) },
        { title: 'Taxe', dataIndex: 'taxe', render: (v: number) => formatNumber(v) },
        { title: 'Annuité TTC', dataIndex: 'anTTC', render: (v: number) => formatNumber(v) },
    ];

    const canSubmit = mode === 'simu' ? !!selectedKey : preview.length > 0;

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b>Demande de rééchelonnement</b></Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <Form form={form} layout="vertical" labelAlign='left'>
                    <div className='bG'>
                        <legend><Typography color={"#116599"}>Actions</Typography></legend>
                        <Form.Item shouldUpdate className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {mode === 'simu' ? (
                                    <Button size='large' className='bt_add' onClick={() => {
                                        if (!form.getFieldValue('NUMDOS')) { SweetAlert.fire({ title: "Sélectionnez un dossier de prêt", icon: "warning" }); return; }
                                        consult();
                                        loadSimus();
                                    }} type="ghost" htmlType="button" block>Charger simulations</Button>
                                ) : (
                                    <Button size='large' className='bt_add' onClick={() => {
                                        if (!form.getFieldValue('NUMDOS')) { SweetAlert.fire({ title: "Sélectionnez un dossier de prêt", icon: "warning" }); return; }
                                        doPreview();
                                    }} type="ghost" htmlType="button" block>Aperçu échéancier</Button>
                                )}
                                <Button size='large' className='bt_add' disabled={!canSubmit} onClick={() => {
                                    const doSoumettre = () => callApi('SOUMETTRE', {}, (res) => {
                                        SweetAlert.fire({ title: res.Message, icon: "success" });
                                        if (mode === 'simu') loadSimus();
                                        setPreview([]);
                                    });
                                    if (mode === 'saisie') {
                                        previewCapital(doSoumettre);
                                    } else {
                                        SweetAlert.fire({
                                            title: `Soumettre la simulation ${selectedKey} comme demande ?`,
                                            text: "La demande passera en statut EN ATTENTE pour validation.",
                                            showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non",
                                            confirmButtonColor: "#116599", icon: 'question'
                                        }).then((r) => { if (r.isConfirmed) doSoumettre(); });
                                    }
                                }} type="ghost" htmlType="button" block>Soumettre</Button>
                                {mode === 'simu' && (
                                    <Button size='large' className='bt_cancel' style={{ color: '#fff' }} disabled={!selectedKey} onClick={() => {
                                        SweetAlert.fire({
                                            title: `Annuler la simulation ${selectedKey} ?`,
                                            showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non",
                                            confirmButtonColor: "#a41b1b", icon: 'warning'
                                        }).then((r) => { if (r.isConfirmed) callApi('ANNULE', {}, () => { SweetAlert.fire({ title: "Simulation annulée.", icon: "success" }); loadSimus(); }); });
                                    }} type="ghost" htmlType="button" block>Annuler simu</Button>
                                )}
                                <Button size='large' className='bt_add' onClick={() => { form.resetFields(); setPret(null); setSimus([]); setDetail(null); setSelectedKey(null); setPreview([]); }} type="ghost" htmlType="reset" block>Actualiser</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <div style={{ width: '88%' }}>
                            <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                                <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                    <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Dossier de prêt</legend>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '35% minmax(0, 1fr)', gap: 1, alignItems: 'center' }}>
                                        <Box sx={{ textAlign: 'left', pr: 1 }}>
                                            <span style={{ color: 'red', marginRight: 4 }}>*</span>Numéro dossier
                                        </Box>
                                        <Box sx={{ minWidth: 0, width: '100%' }}>
                                            <SearchInput width={0} form={form} Titre='Recherche dossier' label='' name='NUMDOS' Parametre={searchPret} handleFunction={consult} />
                                        </Box>
                                    </Box>
                                    <Radio.Group style={{ marginTop: 8 }} value={mode}
                                        onChange={(e) => { setMode(e.target.value); setDetail(null); setPreview([]); setSelectedKey(null); }}>
                                        <Radio.Button value="simu">Depuis une simulation</Radio.Button>
                                        <Radio.Button value="saisie">Saisie directe</Radio.Button>
                                    </Radio.Group>
                                </fieldset>

                                {pret && (
                                    <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Détail du prêt</legend>
                                        <Descriptions size="small" column={2} bordered>
                                            <Descriptions.Item label="Client">{pret.NOMCLI} ({pret.NUMCLI})</Descriptions.Item>
                                            <Descriptions.Item label="Type de prêt">{pret.TYPEPRET}</Descriptions.Item>
                                            <Descriptions.Item label="Montant initial">{formatNumber(pret.MONTAN)}</Descriptions.Item>
                                            <Descriptions.Item label="Taux intérêt">{pret.TAUXINT}%</Descriptions.Item>
                                            <Descriptions.Item label="Échéances">{pret.NBRECHE}</Descriptions.Item>
                                            <Descriptions.Item label="Périodicité">{pret.PERIODIC}</Descriptions.Item>
                                            <Descriptions.Item label="Solde capital restant">{formatNumber(pret.RESTANTDU ?? 0)}</Descriptions.Item>
                                            <Descriptions.Item label="Échéances impayées">{pret.NBECHIMP ?? 0}</Descriptions.Item>
                                            <Descriptions.Item label="Statut">{pret.LIBETAT}</Descriptions.Item>
                                            <Descriptions.Item label="Prochaine échéance">{pret.PROCHAINEECH ? String(pret.PROCHAINEECH).substring(0, 10) : '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Date validation">{pret.DateVal ? String(pret.DateVal).substring(0, 10) : '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Validateur">{pret.UTIVAL ?? '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Gestionnaire">{pret.LIBCODGES || pret.CODGES || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Éch. payées">{`${pret.NBECHPAYE ?? 0} — ${formatNumber(pret.MTNECHPAYE ?? 0)}`}</Descriptions.Item>
                                            <Descriptions.Item label="Éch. non payées">{`${(pret.NBECHTOT ?? 0) - (pret.NBECHPAYE ?? 0)} — ${formatNumber(pret.MTNECHIMPAYE ?? 0)}`}</Descriptions.Item>
                                        </Descriptions>
                                    </fieldset>
                                )}

                                {mode === 'saisie' && (
                                    <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Nouveau calendrier</legend>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                                            <Form.Item name="NBRECHE_NEW" label="Nouveau nombre d'échéances" rules={[{ required: true, message: 'Obligatoire' }]}>
                                                <InputNumber min={1} style={{ width: '100%' }} />
                                            </Form.Item>
                                            <SearchModal width={9} form={form} Titre='Périodicité' label='Nouvelle périodicité' name='PERIODIC_NEW' Parametre={searchPeriod} />
                                            <Form.Item name="DAT1ECHE_NEW" label="Nouvelle 1ère échéance" rules={[{ required: true, message: 'Obligatoire' }]}>
                                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                            </Form.Item>
                                            <SearchModal width={9} form={form} Titre='Recherche motif' label='Motif du rééchelonnement' name='MOTIF' Parametre={searchMotif} />
                                            <Form.Item name="COMMENTAIRE" label="Commentaire">
                                                <Input />
                                            </Form.Item>
                                        </Box>
                                        {preview.length > 0 && (
                                            <>
                                                <Tag color="orange" style={{ margin: '4px 0 8px' }}>Aperçu prévisionnel (non enregistré)</Tag>
                                                <Table size='small' columns={colsEch} dataSource={preview}
                                                    scroll={{ x: 700, y: 220 }} pagination={false} rowKey="numSeq" />
                                            </>
                                        )}
                                    </fieldset>
                                )}

                                {mode === 'simu' && (
                                    <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Simulations enregistrées</legend>
                                        {simus.length === 0 ? (
                                            <Typography variant="caption" color="textSecondary">
                                                Sélectionnez un dossier puis « Charger simulations » pour afficher les simulations.
                                            </Typography>
                                        ) : (
                                            <Table size='small' columns={colsSimu} dataSource={simus}
                                                scroll={{ x: 900, y: 200 }} pagination={false} rowKey="Id"
                                                rowSelection={{
                                                    type: 'radio',
                                                    selectedRowKeys: selectedKey ? [selectedKey] : [],
                                                    onChange: (keys) => {
                                                        setSelectedKey(keys.length ? keys[0] : null);
                                                        if (keys.length) loadDetail(keys[0]);
                                                    }
                                                }} />
                                        )}
                                    </fieldset>
                                )}

                                {mode === 'simu' && detail?.demande && (
                                    <fieldset className="bordure1" style={{ width: '100%' }}>
                                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Détail de la simulation {detail.demande.Id}</legend>
                                        <Descriptions size="small" column={3} bordered style={{ marginBottom: 8 }}>
                                            <Descriptions.Item label="Nouveau capital">{formatNumber(detail.demande.MNTCAPIT)}</Descriptions.Item>
                                            <Descriptions.Item label="Échéances">{detail.demande.NBRECHE}</Descriptions.Item>
                                            <Descriptions.Item label="Périodicité">{detail.demande.PERIODIC}</Descriptions.Item>
                                            <Descriptions.Item label="Impayés capitalisés">{formatNumber(detail.demande.MTNIMPAYE)}</Descriptions.Item>
                                            <Descriptions.Item label="Frais">{formatNumber(detail.demande.MTNFRAIS)}</Descriptions.Item>
                                            <Descriptions.Item label="Motif">{detail.demande.MOTIF}</Descriptions.Item>
                                        </Descriptions>
                                        <Table size='small' columns={colsEch} dataSource={detail.echeances}
                                            scroll={{ x: 700, y: 200 }} pagination={false} rowKey="numSeq" />
                                    </fieldset>
                                )}
                            </div>
                        </div>
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default DemandeReechelonnement;
